import { useState } from "react";

const COLORS = {
  cream: "#FDF8F2", sage: "#7A9E8E", sageDark: "#4A7A68", sageLight: "#A8C4B8",
  terra: "#C4765A", gold: "#C9A84C", ink: "#1E2A24", muted: "#6B7C72",
  green: "#5A9B72", red: "#C45A5A", white: "#FFFFFF"
};

const NAV_ITEMS = [
  { id: "timeline", label: "📅 Timeline" },
  { id: "nutrition", label: "🥗 Nutrition" },
  { id: "exercise", label: "💪 Exercise" },
  { id: "supplements", label: "💊 Supplements" },
  { id: "hospitals", label: "🏥 Hospitals" },
  { id: "emergency", label: "🚨 Emergency" },
  { id: "resources", label: "🔗 Resources" },
  { id: "tips", label: "✨ Tips" },
  { id: "checklist", label: "🧳 Hospital Bag" },
  { id: "shop", label: "🛍️ Shop" },
  { id: "blog", label: "📝 Blog" },
  { id: "about", label: "👤 About" },
  { id: "privacy", label: "🔒 Privacy" },
  { id: "contact", label: "📬 Contact" },
];


const CHECKLIST_CATEGORIES = [
  {
    id: "mom", label: "👩 For Mom", color: "#9B8EC4",
    items: [
      { id: "m1", text: "OHIP card & photo ID" },
      { id: "m2", text: "Hospital pre-registration papers" },
      { id: "m3", text: "Birth plan (printed copy)" },
      { id: "m4", text: "2–3 loose comfortable nightgowns or pyjamas" },
      { id: "m5", text: "Warm robe & slippers" },
      { id: "m6", text: "Going-home outfit (maternity size)" },
      { id: "m7", text: "Nursing bra x2 & breast pads" },
      { id: "m8", text: "Nipple cream (lanolin)" },
      { id: "m9", text: "Toothbrush, toothpaste & toiletries" },
      { id: "m10", text: "Shampoo, conditioner & body wash" },
      { id: "m11", text: "Lip balm & hair ties" },
      { id: "m12", text: "Glasses or contact lenses" },
      { id: "m13", text: "Phone & charger + power bank" },
      { id: "m14", text: "Pillow from home (pack in coloured case)" },
      { id: "m15", text: "Snacks for early labour (granola bars, crackers)" },
      { id: "m16", text: "Headphones & comfort playlist" },
    ]
  },
  {
    id: "baby", label: "👶 For Baby", color: "#7A9E8E",
    items: [
      { id: "b1", text: "2–3 onesies (newborn AND 0–3 month sizes)" },
      { id: "b2", text: "1–2 sleepers / footie pyjamas" },
      { id: "b3", text: "Receiving blanket" },
      { id: "b4", text: "Newborn hat & socks" },
      { id: "b5", text: "Car seat installed & inspected ✅" },
      { id: "b6", text: "Coming-home outfit (have 2 sizes ready)" },
    ]
  },
  {
    id: "partner", label: "🧑 For Partner", color: "#C9A84C",
    items: [
      { id: "p1", text: "Change of clothes & toiletries (overnight ready)" },
      { id: "p2", text: "Snacks & cash/card for hospital cafeteria" },
      { id: "p3", text: "Phone charger" },
      { id: "p4", text: "Pillow & blanket (hospital chairs are hard!)" },
      { id: "p5", text: "Camera or phone with storage space cleared" },
      { id: "p6", text: "List of people to call/text after birth" },
      { id: "p7", text: "Parking change or Presto card for transit" },
    ]
  },
  {
    id: "carseat", label: "🚗 Car Seat", color: "#C4765A",
    items: [
      { id: "c1", text: "Installed in back seat (never front)" },
      { id: "c2", text: "Rear-facing position (required for newborns)" },
      { id: "c3", text: "Recline angle 30–45° (check built-in indicator)" },
      { id: "c4", text: "Moves less than 1 inch side-to-side when tested" },
      { id: "c5", text: "Using LATCH or seat belt (not both)" },
      { id: "c6", text: "Harness straps at or below baby's shoulders" },
      { id: "c7", text: "Pinch test passed (no loose fabric at collarbone)" },
      { id: "c8", text: "Seat not expired (check moulded date on plastic)" },
      { id: "c9", text: "Professionally inspected (Toronto Fire Services or SHN)" },
    ]
  },
];


const SHOP_PRODUCTS = [
  {
    category: "💊 Supplements & Vitamins",
    color: "#7A9E8E",
    items: [
      {
        name: "Prenatal Vitamins",
        desc: "Essential daily prenatal vitamins with folic acid, iron, and DHA — supports baby's brain and neural tube development.",
        tag: "Best Seller",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4ctBnXs",
        emoji: "💊",
        note: "Start from day 1 of pregnancy"
      },
      {
        name: "Folic Acid Supplement",
        desc: "High-quality folic acid supplement — critical for preventing neural tube defects in the first trimester.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4fKAKej",
        emoji: "🧬",
        note: "0.4–1mg daily recommended"
      },
      {
        name: "Magnesium Glycinate",
        desc: "Gentle magnesium supplement for pregnancy leg cramps, better sleep, and blood pressure support.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/3TIBnN2",
        emoji: "💤",
        note: "350mg daily recommended"
      },
      {
        name: "Omega-3 / DHA",
        desc: "Plant-based or fish-derived DHA for baby's brain and eye development throughout pregnancy.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4z6Fna9",
        emoji: "🐟",
        note: "200–300mg DHA daily"
      },
    ]
  },
  {
    category: "🤰 Maternity Comfort",
    color: "#9B8EC4",
    items: [
      {
        name: "Pregnancy Pillow",
        desc: "Full body pregnancy pillow for comfortable side sleeping — relieves back, hip, and belly pressure.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4z3tnWT",
        emoji: "🛌",
        note: "Start using from week 20+"
      },
      {
        name: "Stretch Mark Cream",
        desc: "Moisturizing belly cream to prevent and reduce stretch marks on belly, hips, and breasts.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4wjKfpU",
        emoji: "🧴",
        note: "Apply daily from first trimester"
      },
      {
        name: "Maternity Support Belt",
        desc: "Belly support band that relieves lower back and pelvic pain as your bump grows.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4wexLzK",
        emoji: "🩱",
        note: "Helpful from week 20 onward"
      },
      {
        name: "Nursing Bra",
        desc: "Comfortable wireless nursing bra — essential for hospital bag and postpartum breastfeeding.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4wl35Nj",
        emoji: "👙",
        note: "Pack 2 in your hospital bag"
      },
    ]
  },
  {
    category: "👶 Baby Essentials",
    color: "#C4765A",
    items: [
      {
        name: "Infant Car Seat",
        desc: "Safety-rated rear-facing infant car seat — must be installed before leaving the hospital.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4q3KZ0A",
        emoji: "🚗",
        note: "Install by week 36 and get it inspected"
      },
      {
        name: "Baby Monitor",
        desc: "Video baby monitor with night vision and temperature display — peace of mind for new parents.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/45RJsS0",
        emoji: "📹",
        note: "Set up before baby arrives"
      },
      {
        name: "Breast Pump",
        desc: "Electric breast pump for comfortable, efficient milk expression — great for returning to work.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/3TZWDxF",
        emoji: "🍼",
        note: "Check if covered by your insurance"
      },
      {
        name: "Baby Stroller",
        desc: "Lightweight foldable stroller compatible with most infant car seats — great for Toronto sidewalks.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/45JEerA",
        emoji: "🛺",
        note: "Look for travel system compatibility"
      },
    ]
  },
  {
    category: "📚 Books & Learning",
    color: "#C9A84C",
    items: [
      {
        name: "What to Expect When Expecting",
        desc: "The most trusted pregnancy book — week-by-week guidance from conception through birth.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4hVyh22",
        emoji: "📖",
        note: "The #1 pregnancy book worldwide"
      },
      {
        name: "The Expectant Father",
        desc: "A must-read guide for dads and partners — how to support your partner through every trimester.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4fZkoNE",
        emoji: "👨",
        note: "Perfect for supportive partners"
      },
      {
        name: "Pregnancy Journal",
        desc: "Beautiful keepsake journal to document your pregnancy journey — bump photos, milestones, and memories.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4xqSSzI",
        emoji: "📝",
        note: "A treasure to look back on"
      },
      {
        name: "Baby Name Book",
        desc: "Comprehensive baby name guide with meanings, origins, and inspiration for Canadian families.",
        tag: "Live ✅",
        tagColor: "#5A9B72",
        link: "https://amzn.to/4wOG7iL",
        emoji: "✨",
        note: "Start the fun conversation early"
      },
    ]
  },
];


const BLOG_ARTICLES = [
  {
    id: 1,
    emoji: "🤰",
    tag: "Prenatal Care",
    tagColor: "#7A9E8E",
    title: "Complete Guide to Prenatal Care in Ontario",
    date: "August 2026",
    readTime: "8 min read",
    summary: "Everything you need to know about prenatal care in Ontario — from your first appointment to delivery, including what OHIP covers.",
    content: `Prenatal care is one of the most important investments you can make for the health of both mother and baby. In Ontario, the healthcare system provides comprehensive prenatal services covered under OHIP, making quality care accessible to all residents.

YOUR FIRST PRENATAL APPOINTMENT

Your first prenatal visit should be booked as soon as you confirm your pregnancy — ideally before week 8. During this visit your doctor will confirm your pregnancy, calculate your due date based on your last menstrual period (LMP), order your first blood tests, discuss your medical history, and prescribe prenatal vitamins if you haven't started already.

Blood tests at your first visit include blood type and Rh factor, complete blood count (CBC), rubella immunity, hepatitis B and C screening, HIV and syphilis testing, thyroid function, and blood glucose levels. All of these are fully covered by OHIP.

CHOOSING YOUR CARE PROVIDER

In Ontario you have several options for prenatal care. A family doctor or GP can provide prenatal care and refer you to an OB-GYN when needed. An obstetrician (OB-GYN) specializes in pregnancy and birth and is recommended for high-risk pregnancies. A midwife provides personalized care throughout pregnancy and birth — midwifery care is fully covered by OHIP in Ontario and is an excellent choice for low-risk pregnancies.

PRENATAL VISITS SCHEDULE

The typical schedule for prenatal visits in Ontario follows a standard pattern. From weeks 4 to 28 you will have appointments every 4 weeks. From weeks 28 to 36 visits increase to every 2 weeks. From week 36 to delivery you will be seen every week. At each visit your doctor or midwife will check your blood pressure, weight, and urine. They will measure your fundal height to track baby's growth, check baby's heartbeat, and address any concerns or questions you have.

OHIP COVERAGE FOR PRENATAL CARE

Ontario's health insurance plan covers a wide range of prenatal services at no cost. This includes all prenatal doctor and midwife visits, standard blood tests and urine tests, ultrasounds including the dating scan and anatomy scan, genetic screening tests, Group B Strep testing, Rh factor injection (WinRho) if needed, and labour and delivery at any Ontario hospital.

Some services are not covered by OHIP including the NIPT (Non-Invasive Prenatal Test) which costs approximately $500 to $700, private room upgrades at hospital, and some specialized genetic counselling services.

PRENATAL VITAMINS IN ONTARIO

Health Canada recommends all pregnant women take a daily prenatal vitamin containing at least 0.4mg of folic acid, iron, vitamin D at 400 IU minimum, calcium, and DHA or omega-3 fatty acids. Starting folic acid before conception and in the first trimester is particularly important as it significantly reduces the risk of neural tube defects in the developing baby.

FINDING CARE IN SCARBOROUGH AND TORONTO

The Scarborough Health Network operates two main sites offering comprehensive maternity care — Birchmount Hospital at 3030 Birchmount Road and Scarborough General at 3050 Lawrence Avenue East. Michael Garron Hospital at 825 Coxwell Avenue and North York General at 4001 Leslie Street are also excellent options for families in the greater Toronto area.

To find a midwife in your area visit the Association of Ontario Midwives website or ask your family doctor for a referral. Midwifery practices in Scarborough often have waitlists so it is best to register early in your pregnancy.`
  },
  {
    id: 2,
    emoji: "🥗",
    tag: "Nutrition",
    tagColor: "#C9A84C",
    title: "What to Eat During Each Trimester in Canada",
    date: "August 2026",
    readTime: "7 min read",
    summary: "A complete trimester-by-trimester nutrition guide for Canadian pregnant women — including foods to eat, foods to avoid, and key nutrients.",
    content: `Good nutrition during pregnancy is one of the most powerful things you can do for your baby's development and your own health. This guide breaks down exactly what to focus on during each trimester of pregnancy with practical tips for Canadian families.

FIRST TRIMESTER NUTRITION (WEEKS 1 TO 12)

The first trimester is when your baby's neural tube, brain, heart, and major organs are forming. This makes nutrition critically important even before you may feel pregnant.

The most important nutrient in the first trimester is folic acid. Health Canada recommends at least 0.4mg daily — ideally starting before conception and continuing through the first trimester. Folic acid is found naturally in leafy greens like spinach and kale, lentils and chickpeas, fortified cereals and bread, and okra which is an excellent source.

Many women experience morning sickness in the first trimester which can make eating challenging. If this is the case eat small frequent meals every 2 to 3 hours, keep crackers or plain toast nearby for nausea, try ginger tea or ginger candies for relief, eat cold foods which have less smell than hot foods, and stay hydrated with small sips of water throughout the day.

Foods to focus on in the first trimester include leafy greens for folate, eggs for choline and protein, legumes for iron and folate, whole grains for fiber and B vitamins, and dairy or fortified plant milk for calcium.

SECOND TRIMESTER NUTRITION (WEEKS 13 TO 26)

The second trimester is often called the golden trimester as morning sickness usually eases and energy returns. This is when your baby grows rapidly and your blood volume increases significantly — making iron particularly important.

Iron requirements increase substantially during pregnancy. Good sources of iron include lean red meat, chicken and turkey, lentils and beans, spinach and dark leafy greens, and fortified cereals. Eat iron-rich foods alongside vitamin C sources like orange juice or bell peppers to enhance absorption.

Calcium becomes increasingly important as your baby's bones and teeth begin to harden. Aim for 1000mg of calcium daily from dairy products, fortified plant milk, broccoli, almonds, and chia seeds.

Omega-3 fatty acids are essential for your baby's brain and eye development. Eat salmon two to three times per week, add chia seeds to smoothies and yogurt, and consider a DHA supplement if you don't eat fish regularly.

THIRD TRIMESTER NUTRITION (WEEKS 27 TO 40)

In the third trimester your baby gains most of their birth weight and their brain develops rapidly. Your caloric needs increase by approximately 450 calories per day above your pre-pregnancy intake.

Focus on protein-rich foods to support your baby's rapid growth. Good sources include eggs, Greek yogurt, chicken, lentils, tofu, and nuts and seeds. Aim for at least 70 to 100 grams of protein daily.

Constipation is very common in the third trimester due to the pressure of your growing uterus and the effect of pregnancy hormones on digestion. Combat this by drinking 8 to 10 glasses of water daily, eating high fiber foods like whole grains, fruits and vegetables, and prunes, and staying physically active with gentle walking.

FOODS TO AVOID THROUGHOUT PREGNANCY IN CANADA

Health Canada advises pregnant women to avoid raw or undercooked meat and fish, unpasteurized dairy products and juices, raw sprouts, high mercury fish including shark swordfish and king mackerel, deli meats and hot dogs unless heated to steaming, and alcohol completely. Caffeine should be limited to under 200mg per day which is approximately one small cup of coffee.

STAYING WELL HYDRATED IN CANADA

Canadian winters can be deceptively dehydrating due to dry indoor heating. Aim for 8 to 10 glasses of water daily year round. Herbal teas like ginger and peppermint are safe in moderation and can help with nausea and digestion. Avoid herbal teas with licorice root, sage, or high doses of ginger during pregnancy.`
  },
  {
    id: 3,
    emoji: "🏥",
    tag: "Toronto Guide",
    tagColor: "#C4765A",
    title: "How to Choose a Hospital for Birth in Toronto",
    date: "August 2026",
    readTime: "6 min read",
    summary: "A practical guide to choosing the right hospital or birth centre for your delivery in Toronto and Scarborough — what to look for and questions to ask.",
    content: `Choosing where to give birth is one of the most important decisions you will make during your pregnancy. In Toronto and the greater Scarborough area there are several excellent hospitals and birth centres each with their own strengths and specialties.

HOSPITALS IN THE SCARBOROUGH AND TORONTO AREA

Scarborough Health Network Birchmount Hospital located at 3030 Birchmount Road is the closest full-service maternity hospital for most Scarborough families. It offers labour and delivery services, a special care nursery, postpartum care, and lactation support. The phone number for labour and delivery is 416-495-2400.

Scarborough Health Network General Hospital at 3050 Lawrence Avenue East also provides maternity services and is convenient for families in the south Scarborough area. Michael Garron Hospital at 825 Coxwell Avenue in East Toronto offers a full obstetric program including a level 2 special care nursery making it a good choice for pregnancies with some complications.

North York General Hospital at 4001 Leslie Street has an excellent birth centre and level 3 NICU which is one of the most advanced neonatal intensive care units in the region — making it ideal for high-risk pregnancies or babies who may need extra support after birth. Mount Sinai Hospital in downtown Toronto is a world leader in maternal fetal medicine and high-risk obstetrics and is where many complex pregnancies in Ontario are referred.

WHAT TO LOOK FOR IN A BIRTH HOSPITAL

When choosing your birth hospital consider the level of NICU available. A level 1 nursery handles healthy full term babies. A level 2 special care nursery handles babies born after 32 weeks with moderate complications. A level 3 NICU handles the most premature and critically ill newborns. If your pregnancy is low risk a hospital with a level 1 or level 2 nursery is perfectly appropriate.

Consider also the hospital's epidural availability — most Toronto hospitals offer epidurals 24 hours a day 7 days a week. Ask about the hospital's caesarean section rate, whether they support skin to skin contact immediately after birth, their breastfeeding support and lactation consultant availability, visitor policies including whether your partner can stay overnight, and the availability of private postpartum rooms.

PRE-REGISTERING AT YOUR HOSPITAL

In Ontario you can and should pre-register at your chosen hospital before you go into labour. Contact the hospital's registration or admissions department around week 32 to 34. Pre-registration saves valuable time when you arrive in labour and ensures your information is already in their system. Bring your OHIP card, identification, and your doctor's or midwife's name and contact information.

TOURING YOUR HOSPITAL

Most hospitals in the Toronto area offer prenatal tours of their labour and delivery unit. These tours are highly recommended as they help you familiarize yourself with the facility, understand the check-in process, see what the labour rooms and postpartum rooms look like, and reduce anxiety about the unknown on your due date. Contact your chosen hospital to book a tour — many are now offered virtually as well.

CHOOSING BETWEEN HOSPITAL AND MIDWIFE-LED BIRTH CENTRE

In Ontario midwives can attend births at home, in hospital, or at a birth centre. For low-risk pregnancies a midwife-attended birth is a safe and fully OHIP-covered option. Midwives provide continuous one-on-one care during labour which many women find reassuring. If complications arise during labour your midwife will transfer care to the hospital obstetric team.

WHEN TO GO TO THE HOSPITAL

Know in advance when your care provider wants you to come to the hospital. A general rule for first time mothers is to go when contractions are 5 minutes apart lasting 1 minute each for at least 1 hour — known as the 5-1-1 rule. Always go immediately if your water breaks, if you have heavy bleeding, if you notice significantly reduced baby movement, or if you have a severe headache with vision changes.`
  },
  {
    id: 4,
    emoji: "💊",
    tag: "Health",
    tagColor: "#9B8EC4",
    title: "Understanding OHIP Coverage During Pregnancy in Ontario",
    date: "August 2026",
    readTime: "5 min read",
    summary: "A clear breakdown of what OHIP covers during pregnancy in Ontario — what's free, what costs money, and how to maximize your benefits.",
    content: `Ontario's Health Insurance Plan — known as OHIP — provides comprehensive coverage for most pregnancy-related medical care. Understanding exactly what is covered and what is not can help you plan financially and avoid unexpected costs during your pregnancy.

WHAT OHIP COVERS DURING PREGNANCY

OHIP covers the vast majority of prenatal care at no direct cost to eligible Ontario residents. This includes all prenatal visits with your family doctor, OB-GYN, or registered midwife, all standard laboratory blood tests ordered by your doctor, urine testing throughout pregnancy, all standard ultrasounds including your dating scan at 10 to 14 weeks and anatomy scan at 18 to 20 weeks, nuchal translucency ultrasound at 11 to 13 weeks, maternal serum screening blood tests, Group B Streptococcus testing at 35 to 37 weeks, WinRho injection for Rh-negative mothers at 28 weeks, all labour and delivery care at any Ontario hospital, epidural and other pain management during labour, caesarean section if medically required, and postpartum hospital care for both mother and baby.

WHAT IS NOT COVERED BY OHIP

Some important pregnancy-related services are not covered by OHIP and come with out-of-pocket costs. The Non-Invasive Prenatal Test commonly called NIPT screens for chromosomal conditions like Down syndrome Trisomy 18 and Trisomy 13. This blood test costs approximately $500 to $700 in Ontario and is not covered unless you meet specific criteria such as being over 40 years old or having a previous affected pregnancy.

Private hospital rooms are not covered by OHIP. A standard ward with shared facilities is covered but upgrading to a semi-private or private room costs $150 to $400 per day depending on the hospital. Many private health insurance plans from employers cover this upgrade so check your benefits before delivery.

Prenatal vitamins and supplements are not covered by OHIP though some may be available through the Ontario Drug Benefit program if you qualify based on income. Childbirth education classes offered by hospitals or private providers typically cost $150 to $400 for a complete series though some free options are available through Toronto Public Health. Parking at Ontario hospitals is also not covered and can be expensive — check if your hospital offers a monthly parking pass for frequent prenatal visits.

REGISTERING FOR OHIP IN ONTARIO

To access OHIP coverage you must be an Ontario resident with a valid health card. New residents to Ontario must wait 3 months after establishing residency before OHIP coverage begins. During this waiting period you will need private health insurance. If you are a newcomer to Canada check with a settlement agency about options for coverage during the waiting period.

MAXIMIZING YOUR BENEFITS IN ONTARIO

Beyond OHIP there are additional financial supports available to pregnant women and new parents in Ontario. The Canada Child Benefit provides tax-free monthly payments to eligible families — apply as soon as your baby is born. Employment Insurance maternity and parental benefits provide income replacement for up to 18 months after birth. The Healthy Babies Healthy Children program offers free home visits and support for new parents. Ontario Works and the Ontario Disability Support Program provide additional supports for those who qualify.

KEEPING YOUR RECEIPTS

For expenses not covered by OHIP keep all receipts for tax purposes. Many pregnancy and newborn expenses are tax-deductible in Canada including prenatal classes, some medical equipment, travel costs to medical appointments, and private health insurance premiums. Consult a tax professional or use the Canada Revenue Agency website for the most current information on eligible medical expenses.`
  },
  {
    id: 5,
    emoji: "✨",
    tag: "Tips",
    tagColor: "#4A7A68",
    title: "Top 15 Pregnancy Tips for First Time Moms in Canada",
    date: "August 2026",
    readTime: "6 min read",
    summary: "Practical, honest pregnancy tips specifically for first time moms in Canada — from a supportive community of parents who have been through it.",
    content: `Pregnancy is one of life's most transformative journeys. For first time mothers especially it can feel overwhelming with so much information and advice coming from every direction. Here are 15 practical evidence-based tips specifically relevant for Canadian moms navigating their first pregnancy.

1. START PRENATAL VITAMINS BEFORE YOU NEED THEM

If you are planning a pregnancy start taking prenatal vitamins containing folic acid before you conceive. Neural tube defects develop in the first few weeks of pregnancy — often before women even know they are pregnant. Health Canada recommends 0.4mg of folic acid daily for all women of childbearing age.

2. REGISTER WITH A MIDWIFE EARLY

Midwifery care is fully covered by OHIP in Ontario and offers exceptional one-on-one support throughout pregnancy and birth. However midwifery practices in Toronto and Scarborough often have long waitlists. Register as soon as you confirm your pregnancy — ideally before week 10.

3. UNDERSTAND YOUR WORKPLACE RIGHTS

In Ontario pregnant employees are protected by the Employment Standards Act. You are entitled to 17 weeks of unpaid pregnancy leave followed by up to 61 weeks of parental leave. Through Employment Insurance you can receive maternity benefits for 15 weeks and parental benefits for up to 35 weeks at standard rate or 61 weeks at extended rate. Notify your employer in writing at least 2 weeks before your planned leave begins.

4. BUILD YOUR SUPPORT NETWORK EARLY

Pregnancy and new parenthood are much easier with a strong support network. Connect with other expectant parents through prenatal classes offered by Toronto Public Health which are free of charge. Online communities for Canadian moms such as local Facebook groups and the Baby Centre Canada community can also be invaluable sources of peer support and local advice.

5. ATTEND ALL PRENATAL APPOINTMENTS

Even when you feel well and your pregnancy seems to be going smoothly do not skip prenatal appointments. Many conditions including gestational hypertension gestational diabetes and placenta issues develop without obvious symptoms and are only detected through routine monitoring.

6. SLEEP ON YOUR LEFT SIDE

From the second trimester onward sleeping on your left side improves blood circulation to your baby and kidneys and reduces pressure on your liver. Invest in a good pregnancy pillow to make side sleeping more comfortable — it will become your best friend in the third trimester.

7. STAY ACTIVE THROUGHOUT PREGNANCY

Unless your doctor advises otherwise aim for 30 minutes of moderate physical activity most days of the week. Walking swimming and prenatal yoga are all excellent choices. Regular exercise reduces the risk of gestational diabetes reduces back pain improves sleep quality and can make labour shorter and easier.

8. PREPARE YOUR FINANCES EARLY

Review your budget and start saving for baby expenses well before your due date. Key costs to plan for in Canada include baby gear such as crib car seat and stroller, extended health costs not covered by OHIP, potential income reduction during parental leave, and childcare costs when you return to work — which in Toronto can range from $1500 to $2500 per month for infant care.

9. TAKE A PRENATAL CLASS

Prenatal classes prepare you for labour delivery and early parenthood. Toronto Public Health offers free prenatal education programs across the city. Hospital-based programs typically cost $150 to $400 but provide excellent preparation for birth and breastfeeding. Online options are also available if attending in person is difficult.

10. WRITE A BIRTH PLAN

A birth plan is a document that communicates your preferences for labour and delivery to your healthcare team. It should cover your preferences for pain management including whether you want an epidural, who you want present in the delivery room, your wishes for delayed cord clamping and skin-to-skin contact, breastfeeding intentions, and what to do in case of a caesarean section. Keep it flexible — birth rarely goes exactly to plan.

11. PACK YOUR HOSPITAL BAG BY WEEK 36

Do not leave hospital bag packing to the last minute. Many women go into labour before their due date. Pack by week 36 at the latest. Include your OHIP card and ID, comfortable clothing for labour and postpartum, nursing bras and breast pads, toiletries, phone charger, snacks for your support person, and your baby's going-home outfit in both newborn and 0-3 month sizes.

12. INSTALL YOUR CAR SEAT EARLY

Your baby cannot leave the hospital without an approved rear-facing car seat properly installed in your vehicle. Install it by week 36 and get it inspected for free at a Toronto Fire Services station. Never use a second-hand car seat unless you can verify its complete history.

13. KNOW THE WARNING SIGNS

Learn the warning signs that require immediate medical attention including heavy vaginal bleeding, severe headache with vision changes which may indicate preeclampsia, significantly reduced fetal movement, regular contractions before 37 weeks, and water breaking at any time. When in doubt call Telehealth Ontario at 1-866-797-0000 which is available 24 hours a day 7 days a week at no charge.

14. TAKE CARE OF YOUR MENTAL HEALTH

Pregnancy can bring a range of emotions including anxiety and depression. Perinatal mood disorders affect 1 in 5 Canadian women. Do not hesitate to speak to your doctor or midwife about how you are feeling emotionally. Support is available through your local public health unit, the Postpartum Support International helpline, and mental health programs at Toronto hospitals.

15. ENJOY THE JOURNEY

Amid all the appointments advice and preparation remember to pause and appreciate this remarkable time. Take bump photos, keep a pregnancy journal, share the experience with your partner, and connect with your baby through talking singing and gentle touch. These months go by faster than you expect.`
  },
];

const T1_WEEKS = [
  {
    badge: "Now · Wks 6–8", icon: "🫐", title: "First Prenatal Visit",
    date: "July 2026",
    items: [
      "Book your first OB-GYN or midwife appointment immediately",
      "Start prenatal vitamins with folic acid 0.4–1mg daily",
      "Register at Scarborough Health Network hospital",
      "Stop alcohol, smoking, and betel nut completely",
      "Eat small frequent meals to manage morning sickness",
      "Rest as needed — fatigue is completely normal",
    ],
    tag: "🩺 First blood tests & urine screening"
  },
  {
    badge: "Wks 8–10", icon: "🍇", title: "Blood Work & Screening",
    date: "Late July – August 2026",
    items: [
      "Blood type and Rh factor test (OHIP covered)",
      "CBC, rubella immunity, hepatitis B & C screening",
      "HIV, syphilis, thyroid, and blood sugar tests",
      "Discuss NIPT and genetic screening options with doctor",
      "Avoid raw/undercooked foods and unpasteurized dairy",
    ],
    tag: "🔬 OHIP-covered full blood panel"
  },
  {
    badge: "Wks 11–13", icon: "🍋", title: "First Trimester Screening",
    date: "August – September 2026",
    items: [
      "Nuchal Translucency (NT) Ultrasound — OHIP covered ✅",
      "NIPT blood test (Down syndrome, Trisomy 18/13) — ~$500–700",
      "PAPP-A + hCG blood test combined with NT scan",
      "Baby now has tiny fingers, toes and looks like a human!",
      "Continue avoiding heavy lifting and overheating",
    ],
    tag: "🔬 NT Ultrasound + NIPT available"
  },
];

const T2_WEEKS = [
  {
    badge: "Wks 13–16", icon: "🍎", title: "Golden Trimester Begins",
    date: "September – October 2026",
    items: [
      "Morning sickness usually eases — energy returns!",
      "Prenatal visits every 4 weeks now",
      "Start sleeping on your left side to improve blood flow to baby",
      "Begin stretch mark oil/cream on belly, hips, and breasts",
      "Start prenatal yoga, walking, or swimming",
    ],
    tag: "📅 Visits every 4 weeks"
  },
  {
    badge: "Wks 18–20", icon: "🥭", title: "Anatomy Ultrasound",
    date: "October – November 2026",
    items: [
      "Level 2 anatomy scan — most detailed ultrasound of pregnancy",
      "Checks ALL of baby's organs — fully OHIP covered ✅",
      "You can find out baby's gender if you wish! 👶",
      "Baby starts moving — you may feel flutters for first time!",
      "Begin daily Kegel exercises to strengthen pelvic floor",
    ],
    tag: "🔬 Anatomy Ultrasound — OHIP covered"
  },
  {
    badge: "Wks 24–28", icon: "🌽", title: "Gestational Diabetes Test",
    date: "November – December 2026",
    items: [
      "Glucose Challenge Test (GCT) — drink glucose, blood drawn after 1 hr",
      "WinRho injection if Rh-negative — given at exactly 28 weeks",
      "Baby can now hear your voice — talk and sing to baby! 🎵",
      "Register for free Toronto Public Health prenatal classes",
      "Tour your hospital — Scarborough Health Network",
    ],
    tag: "🔬 GDT + Rh injection at 28 weeks"
  },
];

const T3_WEEKS = [
  {
    badge: "Wks 27–32", icon: "🥥", title: "Vaccines & Kick Counts",
    date: "January 2027",
    items: [
      "Tdap vaccine (whooping cough protection) — free in Ontario ✅",
      "Flu shot if not already done — safe and recommended",
      "Count baby kicks — 10 movements within 2 hours is normal",
      "Prenatal visits now every 2 weeks",
      "Start packing your hospital bag",
      "Pre-register at your chosen hospital before arrival",
    ],
    tag: "💉 Tdap + Flu vaccine recommended"
  },
  {
    badge: "Wks 35–37", icon: "🍈", title: "Group B Strep Test",
    date: "February 2027",
    items: [
      "GBS swab test — simple, painless, and OHIP covered ✅",
      "Finalize your birth plan and discuss with doctor",
      "Install car seat — get it safety-checked at local fire station",
      "Baby is getting into head-down position for birth",
      "Discuss pain management options (epidural vs natural birth)",
    ],
    tag: "🔬 Group B Strep screening"
  },
  {
    badge: "Wks 37–40", icon: "👶", title: "Full Term — Almost There!",
    date: "Late Feb – March 4, 2027",
    items: [
      "Prenatal visits every single week now",
      "Weekly non-stress test (NST) may be ordered by doctor",
      "Know your route to hospital and exactly when to go",
      "Hospital bag packed and car seat installed ✅",
      "Rest, stay hydrated, and trust your body",
      "🎉 Baby arrives around March 4, 2027!",
    ],
    tag: "🏥 Hospital bag ready · Car seat installed"
  },
];

const SAFE_FOODS = [
  { emoji: "🥬", name: "Leafy Greens & Okra", desc: "Rich in folate, iron, calcium — critical for neural tube development" },
  { emoji: "🥑", name: "Avocado", desc: "Healthy fats and folate — great for baby's brain development" },
  { emoji: "🍠", name: "Sweet Potato", desc: "Beta-carotene, fiber, vitamin C" },
  { emoji: "🫐", name: "Berries", desc: "Antioxidants and vitamin C — immune support" },
  { emoji: "🥚", name: "Eggs (fully cooked)", desc: "Choline and protein — supports brain development" },
  { emoji: "🫘", name: "Lentils & Legumes", desc: "Folate, fiber, iron — excellent plant-based nutrition" },
  { emoji: "🐟", name: "Salmon (2–3x/week)", desc: "Omega-3s for baby's brain and eyes" },
  { emoji: "🥛", name: "Greek Yogurt & Dairy", desc: "Calcium, protein, probiotics" },
  { emoji: "🌾", name: "Whole Grains & Oats", desc: "Fiber, B vitamins — steady energy" },
  { emoji: "🌱", name: "Chia Seeds (soaked, 1–2 tbsp/day)", desc: "Omega-3, calcium, iron, fiber — always soak before eating" },
];

const AVOID_FOODS = [
  { emoji: "🍣", name: "Raw Fish & Sushi", desc: "Risk of listeria and parasites" },
  { emoji: "🦈", name: "High Mercury Fish", desc: "Shark, swordfish, king mackerel, tilefish — avoid completely" },
  { emoji: "🧀", name: "Unpasteurized Cheese", desc: "Brie, camembert, blue cheese — listeria risk" },
  { emoji: "🫙", name: "Unripe / Semi-ripe Papaya", desc: "Contains papain — can trigger uterine contractions" },
  { emoji: "🥩", name: "Undercooked Meat", desc: "Salmonella and toxoplasmosis risk" },
  { emoji: "🍺", name: "Alcohol", desc: "No safe level during pregnancy — avoid completely" },
  { emoji: "☕", name: "Excess Caffeine", desc: "Limit to under 200mg/day (~1 small cup of coffee)" },
  { emoji: "🌿", name: "Betel Nut (Supari/Paan)", desc: "Linked to low birth weight, premature birth — avoid completely" },
  { emoji: "🧆", name: "Deli Meats & Hot Dogs", desc: "Unless heated until steaming — listeria risk" },
  { emoji: "🥗", name: "Raw Sprouts", desc: "Alfalfa, radish, mung bean — bacterial contamination risk" },
];

const EXERCISES = [
  {
    label: "First Trimester", weeks: "Weeks 1–12 · July–Sept 2026",
    bg: "linear-gradient(135deg,#E8F2EE,#C8E0D8)", color: COLORS.sageDark,
    list: [
      { icon: "🚶‍♀️", name: "Walking (20–30 mins daily)" },
      { icon: "🧘‍♀️", name: "Prenatal yoga" },
      { icon: "🏊‍♀️", name: "Swimming" },
      { icon: "🤸‍♀️", name: "Light stretching" },
      { icon: "💪", name: "Kegel exercises daily" },
    ],
    avoid: "Heavy lifting, intense cardio, exercises flat on your back"
  },
  {
    label: "Second Trimester", weeks: "Weeks 13–26 · Sept–Dec 2026",
    bg: "linear-gradient(135deg,#FEF9EC,#F5E8C0)", color: "#8B6914",
    list: [
      { icon: "🚶‍♀️", name: "Brisk walking" },
      { icon: "🧘‍♀️", name: "Prenatal pilates & yoga" },
      { icon: "🚴‍♀️", name: "Stationary cycling (no fall risk)" },
      { icon: "🏊‍♀️", name: "Water aerobics" },
      { icon: "🏋️‍♀️", name: "Light strength training" },
    ],
    avoid: "Lying flat on back, contact sports, high fall-risk activities"
  },
  {
    label: "Third Trimester", weeks: "Weeks 27–40 · Jan–Mar 2027",
    bg: "linear-gradient(135deg,#F9EDE8,#F0D0C0)", color: "#8B3A20",
    list: [
      { icon: "🚶‍♀️", name: "Walking (best & safest)" },
      { icon: "🏊‍♀️", name: "Swimming & water aerobics" },
      { icon: "🧘‍♀️", name: "Prenatal yoga & breathing" },
      { icon: "⚽", name: "Birthing ball exercises" },
      { icon: "💪", name: "Kegel exercises daily" },
    ],
    avoid: "Intense exercise, heavy weights, lying on back, fall risks"
  },
];

const SUPPLEMENTS = [
  { icon: "🧬", name: "Folic Acid", dose: "0.4–1mg daily", why: "Neural tube & brain development" },
  { icon: "🩸", name: "Iron", dose: "As prescribed", why: "Prevents anemia, supports oxygen" },
  { icon: "☀️", name: "Vitamin D", dose: "400–2000 IU", why: "Bone development, immunity" },
  { icon: "🐟", name: "DHA / Omega-3", dose: "200–300mg", why: "Baby's brain & eye development" },
  { icon: "🦷", name: "Calcium", dose: "1000mg/day", why: "Bones and teeth for baby" },
  { icon: "💊", name: "Magnesium", dose: "350mg/day", why: "Leg cramps, sleep, blood pressure" },
  { icon: "🧠", name: "Iodine", dose: "In prenatal vitamin", why: "Thyroid & brain development" },
  { icon: "🛡️", name: "Vitamin C", dose: "85mg/day", why: "Immunity, iron absorption" },
];

const HOSPITALS = [
  { name: "SHN Birchmount Hospital", addr: "3030 Birchmount Rd, Scarborough", note: "Labour & Delivery, Maternity — closest to Scarborough", phone: "416-495-2400", maps: "https://www.google.com/maps/search/Scarborough+Health+Network+Birchmount+Hospital" },
  { name: "SHN Scarborough General", addr: "3050 Lawrence Ave E, Scarborough", note: "Maternity services and prenatal care", phone: "416-438-2911", maps: "https://www.google.com/maps/search/Scarborough+General+Hospital" },
  { name: "Michael Garron Hospital", addr: "825 Coxwell Ave, Toronto", note: "Full obstetric services and birthing centre", phone: "416-469-8000", maps: "https://www.google.com/maps/search/Michael+Garron+Hospital+Toronto" },
  { name: "North York General Hospital", addr: "4001 Leslie St, North York", note: "Birth Centre, high-risk obstetrics & NICU", phone: "416-756-6000", maps: "https://www.google.com/maps/search/North+York+General+Hospital" },
  { name: "St. Michael's Hospital", addr: "30 Bond St, Downtown Toronto", note: "High-risk pregnancy specialist centre", phone: "416-360-4000", maps: "https://www.google.com/maps/search/St+Michaels+Hospital+Toronto" },
];

const EMERGENCY_SIGNS = [
  { icon: "🔴", text: "Contractions every 5 minutes for 1+ hour" },
  { icon: "💧", text: "Water breaks — any amount, any colour" },
  { icon: "🩸", text: "Heavy vaginal bleeding — call 911 immediately" },
  { icon: "👶", text: "Baby not moving for 2+ hours" },
  { icon: "🤕", text: "Severe headache with vision changes (preeclampsia)" },
  { icon: "😮‍💨", text: "Chest pain or sudden shortness of breath" },
  { icon: "🌀", text: "Severe dizziness or fainting" },
  { icon: "🤢", text: "Severe abdominal or pelvic pain" },
  { icon: "🦵", text: "Swelling in one leg with pain (blood clot risk)" },
  { icon: "🔥", text: "High fever above 38°C / 100.4°F" },
];

const CONTACTS = [
  { icon: "🚑", name: "Emergency", desc: "Police, Fire, Ambulance", phone: "911", tel: "911" },
  { icon: "🏥", name: "SHN Labour & Delivery", desc: "Scarborough Health Network", phone: "416-495-2400", tel: "4164952400" },
  { icon: "👩‍⚕️", name: "Telehealth Ontario", desc: "24/7 Registered Nurse Line — Free", phone: "1-866-797-0000", tel: "18667970000" },
  { icon: "📱", name: "Health811 Ontario", desc: "Call or text anytime", phone: "811", tel: "811" },
  { icon: "🌿", name: "Toronto Public Health", desc: "Pregnancy health questions", phone: "416-338-7600", tel: "4163387600" },
];

const RESOURCES = [
  { icon: "🍁", tag: "Ontario Gov", name: "Having a Baby in Ontario", desc: "Official Ontario government guide covering prenatal care and OHIP coverage.", url: "https://www.ontario.ca/page/having-baby" },
  { icon: "🏙️", tag: "Toronto", name: "City of Toronto Pregnancy Services", desc: "Free prenatal classes, nutrition counselling, and parenting support.", url: "https://www.toronto.ca/community-people/children-parenting/" },
  { icon: "🏥", tag: "Hospital", name: "SHN Women's Health", desc: "Scarborough Health Network maternity, labour & delivery, postpartum.", url: "https://www.shn.ca/programs-services/womens-health/" },
  { icon: "🤱", tag: "Midwifery", name: "Find a Midwife in Ontario", desc: "Midwifery care is OHIP-covered. Find a registered midwife near Scarborough.", url: "https://www.canadianmidwives.org/find-a-midwife/" },
  { icon: "📖", tag: "Education", name: "Pregnancy Info Canada", desc: "Week-by-week development, symptoms, and evidence-based information.", url: "https://www.pregnancyinfo.ca/" },
  { icon: "⭐", tag: "Support", name: "Best Start Resource Centre", desc: "Ontario maternal and newborn health resources and prenatal education.", url: "https://www.beststart.org/" },
  { icon: "💊", tag: "OHIP", name: "Ontario Drug Benefit", desc: "Check if prenatal vitamins and medications are covered by Ontario.", url: "https://www.health.gov.on.ca/en/public/programs/drugs/odb/" },
  { icon: "🔬", tag: "High Risk", name: "Maternal Fetal Medicine", desc: "Specialists for complex pregnancies, genetic counselling, fetal monitoring.", url: "https://www.mtsinai.on.ca/care-programs/maternal-fetal-medicine" },
];

const TIPS = [
  { icon: "💧", title: "Stay Hydrated", desc: "8–10 glasses of water daily. More in summer or after exercise." },
  { icon: "🛌", title: "Sleep on Left Side", desc: "Improves blood flow to baby and kidneys. Use a pregnancy pillow." },
  { icon: "😌", title: "Manage Stress", desc: "Prenatal yoga, meditation, gentle walks. High stress affects baby." },
  { icon: "🦷", title: "Dental Health", desc: "Pregnancy hormones affect gums. Visit your dentist — it's safe!" },
  { icon: "👟", title: "Wear Comfy Shoes", desc: "Feet may swell. Supportive shoes prevent back and joint pain." },
  { icon: "📝", title: "Keep a Pregnancy Journal", desc: "Track symptoms and questions for your doctor. You'll treasure it." },
  { icon: "🐱", title: "Avoid Cat Litter", desc: "Risk of toxoplasmosis. Have someone else handle the litter box." },
  { icon: "🧴", title: "Safe Skincare Only", desc: "Avoid retinoids and salicylic acid. Use pregnancy-safe products." },
  { icon: "📱", title: "Download a Baby App", desc: "Ovia Pregnancy or The Bump for great week-by-week tracking." },
  { icon: "👨‍👩‍👶", title: "Partner Involvement", desc: "Attend appointments together. Your support makes a real difference 💙" },
  { icon: "🚗", title: "Install Car Seat Early", desc: "Must be ready before leaving hospital. Check at a local fire station." },
  { icon: "📦", title: "Pack Hospital Bag", desc: "Pack by week 36: OHIP card, clothes, charger, snacks for partner." },
];


const SIZES = [
  [4,"🌱","Poppy seed"],[5,"🌿","Sesame seed"],[6,"🫐","Blueberry"],[7,"🫒","Olive"],
  [8,"🍓","Strawberry"],[9,"🍇","Grape"],[10,"🍊","Kumquat"],[11,"🍋","Fig"],
  [12,"🍋","Lime"],[13,"🍑","Peach"],[14,"🍊","Lemon"],[15,"🍎","Apple"],
  [16,"🥑","Avocado"],[17,"🍐","Pear"],[18,"🫑","Bell pepper"],[19,"🥭","Mango"],
  [20,"🍌","Banana"],[21,"🥕","Carrot"],[22,"🌽","Corn"],[23,"🥭","Large mango"],
  [24,"🌽","Ear of corn"],[25,"🥦","Cauliflower"],[26,"🥬","Lettuce head"],
  [27,"🥥","Coconut"],[28,"🍆","Eggplant"],[29,"🎃","Butternut squash"],
  [30,"🥬","Cabbage"],[31,"🥥","Large coconut"],[32,"🫛","Squash"],[33,"🍍","Pineapple"],
  [34,"🍈","Cantaloupe"],[35,"🍈","Honeydew"],[36,"🥬","Romaine lettuce"],
  [37,"🌿","Swiss chard bunch"],[38,"🎃","Mini pumpkin"],[39,"🍉","Watermelon"],
  [40,"🎉","Full term baby!"],
];

function calcPregnancy(lmpStr) {
  if (!lmpStr) return null;
  const LMP = new Date(lmpStr);
  const today = new Date();
  const diffDays = Math.floor((today - LMP) / (1000 * 60 * 60 * 24));
  if (isNaN(diffDays) || diffDays < 0 || diffDays > 294) return null;
  const weeks = Math.min(Math.max(Math.floor(diffDays / 7), 1), 42);
  const days = diffDays % 7;
  const dueDate = new Date(LMP);
  dueDate.setDate(dueDate.getDate() + 280);
  const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  const weeksLeft = Math.max(Math.floor(daysLeft / 7), 0);
  const sizeEntry = SIZES.find(([w]) => w === weeks) || SIZES[SIZES.length - 1];
  const trimester = weeks <= 12 ? "First" : weeks <= 26 ? "Second" : "Third";
  const defaultTab = weeks <= 12 ? "t1" : weeks <= 26 ? "t2" : "t3";
  const dueDateStr = dueDate.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
  return { weeks, days, weeksLeft, sizeEntry, trimester, defaultTab, dueDateStr };
}




function BlogSection() {
  const [selected, setSelected] = useState(null);

  if (selected !== null) {
    const article = BLOG_ARTICLES[selected];
    const paragraphs = article.content.split("\n\n").filter(p => p.trim());
    return (
      <div>
        <button onClick={() => setSelected(null)} style={{ background: "transparent", border: "none", color: COLORS.sageDark, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
          ← Back to all articles
        </button>
        <div style={{ background: "#fff", borderRadius: 20, padding: "2rem", border: "1px solid rgba(122,158,142,0.15)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <span style={{ background: article.tagColor, color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "0.25rem 0.75rem", borderRadius: 50 }}>{article.tag}</span>
            <span style={{ fontSize: "0.75rem", color: COLORS.muted }}>{article.date} · {article.readTime}</span>
          </div>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 400, color: COLORS.ink, marginBottom: "1.5rem", lineHeight: 1.3 }}>{article.emoji} {article.title}</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {paragraphs.map((para, i) => {
              const isHeading = para === para.toUpperCase() && para.length < 80 && !para.includes(".");
              return isHeading ? (
                <h3 key={i} style={{ fontFamily: "Georgia,serif", fontSize: "1rem", fontWeight: 600, color: COLORS.sageDark, marginTop: "0.5rem", textTransform: "capitalize" }}>
                  {para.charAt(0) + para.slice(1).toLowerCase()}
                </h3>
              ) : (
                <p key={i} style={{ fontSize: "0.88rem", color: COLORS.muted, lineHeight: 1.85 }}>{para}</p>
              );
            })}
          </div>
          <div style={{ marginTop: "2rem", padding: "1rem 1.25rem", background: "rgba(122,158,142,0.08)", borderRadius: 12, border: "1px solid rgba(122,158,142,0.2)", fontSize: "0.78rem", color: COLORS.muted }}>
            ⚠️ <strong>Medical Disclaimer:</strong> This article is for informational purposes only and does not constitute medical advice. Always consult your doctor, OB-GYN, or midwife for personal medical guidance during pregnancy.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1.25rem" }}>
      {BLOG_ARTICLES.map((article, i) => (
        <div key={i} onClick={() => setSelected(i)} style={{ background: "#fff", borderRadius: 18, padding: "1.5rem", border: "1px solid rgba(122,158,142,0.15)", cursor: "pointer", transition: "all 0.2s", borderTop: "3px solid " + article.tagColor }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,0.08)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>{article.emoji}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <span style={{ background: article.tagColor, color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "0.2rem 0.6rem", borderRadius: 50 }}>{article.tag}</span>
            <span style={{ fontSize: "0.7rem", color: COLORS.muted }}>{article.readTime}</span>
          </div>
          <h3 style={{ fontFamily: "Georgia,serif", fontSize: "1rem", fontWeight: 400, color: COLORS.ink, marginBottom: "0.6rem", lineHeight: 1.4 }}>{article.title}</h3>
          <p style={{ fontSize: "0.78rem", color: COLORS.muted, lineHeight: 1.6, marginBottom: "1rem" }}>{article.summary}</p>
          <div style={{ color: COLORS.sageDark, fontSize: "0.8rem", fontWeight: 600 }}>Read article →</div>
        </div>
      ))}
    </div>
  );
}

function ShopSection() {
  const [activeTab, setActiveTab] = useState(0);
  const cat = SHOP_PRODUCTS[activeTab];

  return (
    <div>
      {/* Category tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.75rem", flexWrap: "wrap" }}>
        {SHOP_PRODUCTS.map((c, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{ background: activeTab === i ? c.color : "transparent", color: activeTab === i ? "#fff" : COLORS.muted, border: "1.5px solid " + (activeTab === i ? c.color : "rgba(122,158,142,0.3)"), borderRadius: 50, padding: "0.5rem 1.1rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}>
            {c.category}
          </button>
        ))}
      </div>

      {/* Products grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: "1.25rem" }}>
        {cat.items.map((item, i) => (
          <div key={i} style={{ background: COLORS.cream, borderRadius: 18, padding: "1.5rem", border: "1px solid rgba(122,158,142,0.15)", borderTop: "3px solid " + cat.color, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <span style={{ fontSize: "2.25rem" }}>{item.emoji}</span>
              <span style={{ background: item.tagColor, color: "#fff", fontSize: "0.62rem", fontWeight: 700, padding: "0.2rem 0.6rem", borderRadius: 50, letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{item.tag}</span>
            </div>

            {/* Name & desc */}
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: COLORS.ink }}>{item.name}</div>
            <div style={{ fontSize: "0.78rem", color: COLORS.muted, lineHeight: 1.5, flex: 1 }}>{item.desc}</div>

            {/* Note */}
            <div style={{ fontSize: "0.72rem", color: cat.color, fontWeight: 600, background: "rgba(122,158,142,0.08)", padding: "0.35rem 0.6rem", borderRadius: 8 }}>
              💡 {item.note}
            </div>

            {/* Buy button */}
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: "#FF9900", color: "#fff", borderRadius: 50, padding: "0.65rem 1rem", fontSize: "0.82rem", fontWeight: 700, textDecoration: "none", textAlign: "center", display: "block", marginTop: "0.25rem", transition: "opacity 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              🛒 View on Amazon.ca
            </a>

            {/* Affiliate note */}
            <div style={{ fontSize: "0.62rem", color: COLORS.muted, textAlign: "center" }}>
              Affiliate link · Amazon.ca · yourpregnan06-20
            </div>
          </div>
        ))}
      </div>

      {/* Add more products note */}
      <div style={{ marginTop: "1.5rem", background: "rgba(201,168,76,0.08)", borderRadius: 14, padding: "1.1rem 1.5rem", border: "1.5px dashed rgba(201,168,76,0.4)", textAlign: "center" }}>
        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: COLORS.ink, marginBottom: "0.3rem" }}>🛍️ More products coming soon!</div>
        <div style={{ fontSize: "0.78rem", color: COLORS.muted }}>Get SiteStripe links from Amazon.ca and share them to add more products to this section.</div>
      </div>
    </div>
  );
}

function ChecklistSection() {
  const [checked, setChecked] = useState({});
  const [activeTab, setActiveTab] = useState("mom");

  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const cat = CHECKLIST_CATEGORIES.find(c => c.id === activeTab);
  const totalItems = CHECKLIST_CATEGORIES.reduce((sum, c) => sum + c.items.length, 0);
  const totalChecked = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((totalChecked / totalItems) * 100);

  return (
    <div>
      {/* Overall progress */}
      <div style={{ background: "#fff", borderRadius: 16, padding: "1.25rem 1.5rem", marginBottom: "1.5rem", border: "1px solid rgba(122,158,142,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <span style={{ fontWeight: 700, fontSize: "0.9rem", color: COLORS.ink }}>Overall Progress</span>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: COLORS.sageDark }}>{totalChecked} / {totalItems} items · {pct}%</span>
        </div>
        <div style={{ background: "rgba(122,158,142,0.15)", borderRadius: 50, height: 10, overflow: "hidden" }}>
          <div style={{ background: pct === 100 ? COLORS.green : COLORS.sageDark, height: "100%", width: pct + "%", borderRadius: 50, transition: "width 0.4s ease" }} />
        </div>
        {pct === 100 && (
          <div style={{ textAlign: "center", marginTop: "0.75rem", color: COLORS.green, fontWeight: 700, fontSize: "0.9rem" }}>
            🎉 You're all packed and ready! Good luck! 💚
          </div>
        )}
      </div>

      {/* Category tabs */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        {CHECKLIST_CATEGORIES.map(c => {
          const catChecked = c.items.filter(i => checked[i.id]).length;
          const isActive = activeTab === c.id;
          return (
            <button key={c.id} onClick={() => setActiveTab(c.id)} style={{ background: isActive ? c.color : "transparent", color: isActive ? "#fff" : COLORS.muted, border: "1.5px solid " + (isActive ? c.color : "rgba(122,158,142,0.3)"), borderRadius: 50, padding: "0.45rem 1rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "0.4rem", transition: "all 0.2s" }}>
              {c.label}
              <span style={{ background: isActive ? "rgba(255,255,255,0.25)" : "rgba(122,158,142,0.15)", borderRadius: 50, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, color: isActive ? "#fff" : COLORS.sageDark }}>
                {catChecked}/{c.items.length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Items list */}
      <div style={{ background: "#fff", borderRadius: 18, padding: "1.25rem", border: "1px solid rgba(122,158,142,0.15)" }}>
        {/* Category progress bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <span style={{ fontWeight: 700, fontSize: "0.85rem", color: cat.color }}>{cat.label}</span>
          <span style={{ fontSize: "0.75rem", color: COLORS.muted }}>{cat.items.filter(i => checked[i.id]).length} of {cat.items.length} packed</span>
        </div>
        <div style={{ background: "rgba(122,158,142,0.12)", borderRadius: 50, height: 5, marginBottom: "1rem", overflow: "hidden" }}>
          <div style={{ background: cat.color, height: "100%", width: (cat.items.filter(i => checked[i.id]).length / cat.items.length * 100) + "%", borderRadius: 50, transition: "width 0.3s ease" }} />
        </div>

        {/* Checklist items */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {cat.items.map(item => (
            <div key={item.id} onClick={() => toggle(item.id)} style={{ display: "flex", alignItems: "center", gap: "0.85rem", padding: "0.7rem 0.85rem", borderRadius: 10, cursor: "pointer", background: checked[item.id] ? "rgba(90,155,114,0.08)" : "transparent", transition: "background 0.15s", border: "1px solid " + (checked[item.id] ? "rgba(90,155,114,0.2)" : "transparent") }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, border: "2px solid " + (checked[item.id] ? COLORS.green : "rgba(122,158,142,0.4)"), background: checked[item.id] ? COLORS.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                {checked[item.id] && <span style={{ color: "#fff", fontSize: "0.75rem", fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontSize: "0.875rem", color: checked[item.id] ? COLORS.muted : COLORS.ink, textDecoration: checked[item.id] ? "line-through" : "none", transition: "all 0.15s" }}>{item.text}</span>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", paddingTop: "0.85rem", borderTop: "1px solid rgba(122,158,142,0.12)" }}>
          <button onClick={() => { const upd = {...checked}; cat.items.forEach(i => upd[i.id] = true); setChecked(upd); }} style={{ background: cat.color, color: "#fff", border: "none", borderRadius: 50, padding: "0.4rem 1rem", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
            ✓ Check all
          </button>
          <button onClick={() => { const upd = {...checked}; cat.items.forEach(i => delete upd[i.id]); setChecked(upd); }} style={{ background: "transparent", color: COLORS.muted, border: "1px solid rgba(122,158,142,0.3)", borderRadius: 50, padding: "0.4rem 1rem", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
            ✕ Clear all
          </button>
        </div>
      </div>

      {/* Car seat tip */}
      {activeTab === "carseat" && (
        <div style={{ marginTop: "1rem", background: "rgba(196,118,90,0.08)", borderRadius: 14, padding: "1rem 1.25rem", border: "1px solid rgba(196,118,90,0.2)" }}>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: COLORS.terra, marginBottom: "0.35rem" }}>🚗 Get Your Car Seat Inspected in Scarborough</div>
          <div style={{ fontSize: "0.8rem", color: COLORS.muted, lineHeight: 1.6 }}>
            • <strong>Toronto Fire Services</strong> — free car seat checks at local fire stations<br/>
            • <strong>Safe Kids Canada:</strong> <a href="https://www.safekidscanada.ca" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.sageDark }}>safekidscanada.ca</a><br/>
            • <strong>Car seat check locator:</strong> <a href="https://www.cpscanada.ca" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.sageDark }}>cpscanada.ca</a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PregnancyGuide() {
  const [lmp, setLmp] = useState("2026-05-27");
  const [lmpError, setLmpError] = useState("");
  const [activeTrimester, setActiveTrimester] = useState("t1");
  const [activeSection, setActiveSection] = useState("timeline");
  const [selectedHospital, setSelectedHospital] = useState(0);

  const info = calcPregnancy(lmp);

  const handleLmpChange = (val) => {
    setLmpError("");
    if (!val) { setLmpError("Please enter a date."); setLmp(""); return; }
    const d = new Date(val);
    const today = new Date();
    if (isNaN(d.getTime())) { setLmpError("Invalid date."); return; }
    if (d > today) { setLmpError("LMP date cannot be in the future."); return; }
    const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
    if (diff > 294) { setLmpError("Date is over 42 weeks ago — please check and re-enter."); return; }
    setLmp(val);
    const calc = calcPregnancy(val);
    if (calc) setActiveTrimester(calc.defaultTab);
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const s = {
    app: { fontFamily: "system-ui,sans-serif", background: COLORS.cream, color: COLORS.ink, minHeight: "100vh" },
    nav: { position: "sticky", top: 0, zIndex: 100, background: "rgba(253,248,242,0.97)", borderBottom: "1px solid rgba(122,158,142,0.2)", padding: "0 1rem", display: "flex", alignItems: "center", gap: "0.25rem", overflowX: "auto", height: 52, flexShrink: 0 },
    navBtn: (id) => ({ background: activeSection === id ? COLORS.sageDark : "transparent", color: activeSection === id ? "#fff" : COLORS.muted, border: "none", borderRadius: 20, padding: "0.35rem 0.75rem", fontSize: "0.78rem", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }),
    hero: { background: "linear-gradient(135deg,#E8F2EE 0%,#FDF8F2 55%,#F2E8DF 100%)", padding: "2.5rem 1.5rem 3rem" },
    h1: { fontFamily: "Georgia,serif", fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 300, lineHeight: 1.15, marginBottom: "0.5rem" },
    statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", margin: "1.5rem 0" },
    statCard: { background: "#fff", borderRadius: 14, padding: "1rem 1.1rem", border: "1px solid rgba(122,158,142,0.2)" },
    statLabel: { fontSize: "0.65rem", color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.08em" },
    statValue: { fontFamily: "Georgia,serif", fontSize: "1.25rem", color: COLORS.sageDark, marginTop: 2 },
    statSub: { fontSize: "0.7rem", color: COLORS.muted, marginTop: 2 },
    ctaRow: { display: "flex", gap: "0.75rem", flexWrap: "wrap" },
    btnPrimary: { background: COLORS.sageDark, color: "#fff", border: "none", borderRadius: 50, padding: "0.75rem 1.5rem", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" },
    btnSecondary: { background: "transparent", color: COLORS.sageDark, border: `1.5px solid ${COLORS.sageDark}`, borderRadius: 50, padding: "0.75rem 1.5rem", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" },
    section: (bg) => ({ background: bg || COLORS.cream, padding: "3rem 1.5rem" }),
    maxW: { maxWidth: 960, margin: "0 auto" },
    sectionEye: { fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: COLORS.sage, marginBottom: "0.5rem" },
    sectionTitle: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.2rem)", fontWeight: 300, marginBottom: "0.5rem" },
    sectionSub: { color: COLORS.muted, fontSize: "0.9rem", marginBottom: "2rem" },
    tabRow: { display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" },
    tab: (id) => ({ border: `1.5px solid ${id === activeTrimester ? (id === "t1" ? COLORS.sageDark : id === "t2" ? COLORS.gold : COLORS.terra) : "rgba(122,158,142,0.3)"}`, borderRadius: 50, padding: "0.5rem 1.25rem", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", background: id === activeTrimester ? (id === "t1" ? COLORS.sageDark : id === "t2" ? COLORS.gold : COLORS.terra) : "transparent", color: id === activeTrimester ? "#fff" : COLORS.muted, transition: "all 0.2s" }),
    weeksGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1.25rem" },
    weekCard: (tri) => ({ background: COLORS.cream, borderRadius: 18, padding: "1.5rem", border: `1px solid rgba(122,158,142,0.15)`, borderTop: `3px solid ${tri === "t1" ? COLORS.sageDark : tri === "t2" ? COLORS.gold : COLORS.terra}` }),
    badge: (tri) => ({ background: tri === "t1" ? COLORS.sageDark : tri === "t2" ? COLORS.gold : COLORS.terra, color: "#fff", fontSize: "0.65rem", fontWeight: 700, padding: "0.25rem 0.6rem", borderRadius: 50, letterSpacing: "0.04em" }),
    weekTitle: { fontFamily: "Georgia,serif", fontSize: "1.05rem", fontWeight: 400, margin: "0.75rem 0 0.25rem" },
    weekDate: { fontSize: "0.72rem", color: COLORS.sage, fontWeight: 600, marginBottom: "0.75rem" },
    weekItems: { listStyle: "none", padding: 0, margin: 0 },
    weekItem: { fontSize: "0.82rem", color: COLORS.muted, padding: "0.3rem 0", borderBottom: "1px solid rgba(122,158,142,0.1)", display: "flex", gap: "0.4rem", alignItems: "flex-start" },
    testTag: { display: "inline-flex", alignItems: "center", gap: "0.3rem", background: "rgba(122,158,142,0.12)", color: COLORS.sageDark, fontSize: "0.68rem", fontWeight: 700, padding: "0.25rem 0.6rem", borderRadius: 50, marginTop: "0.75rem" },
    foodGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" },
    foodColTitle: { fontFamily: "Georgia,serif", fontSize: "1.2rem", fontWeight: 400, marginBottom: "1rem" },
    foodList: { display: "flex", flexDirection: "column", gap: "0.6rem" },
    foodItem: (safe) => ({ background: "#fff", borderRadius: 12, padding: "0.85rem 1rem", display: "flex", alignItems: "flex-start", gap: "0.75rem", borderLeft: `3px solid ${safe ? COLORS.green : COLORS.red}`, border: "1px solid rgba(122,158,142,0.12)", borderLeftWidth: 3, borderLeftColor: safe ? COLORS.green : COLORS.red }),
    foodEmoji: { fontSize: "1.35rem", flexShrink: 0, marginTop: 1 },
    foodName: { fontSize: "0.85rem", fontWeight: 600, color: COLORS.ink },
    foodDesc: { fontSize: "0.75rem", color: COLORS.muted, marginTop: 1 },
    exGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "1.25rem" },
    exCard: (bg) => ({ borderRadius: 18, padding: "1.75rem 1.5rem", background: bg }),
    exTitle: (c) => ({ fontFamily: "Georgia,serif", fontSize: "1.1rem", fontWeight: 400, color: c }),
    exWeeks: (c) => ({ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: c, opacity: 0.75, margin: "0.25rem 0 1.1rem" }),
    exList: { listStyle: "none", padding: 0 },
    exItem: { fontSize: "0.83rem", color: COLORS.ink, padding: "0.35rem 0", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "0.6rem" },
    exAvoid: { fontSize: "0.75rem", color: COLORS.terra, marginTop: "0.85rem", padding: "0.5rem 0.75rem", background: "rgba(196,118,90,0.1)", borderRadius: 8 },
    suppGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: "0.9rem" },
    suppCard: { background: "#fff", borderRadius: 14, padding: "1.35rem 1.1rem", textAlign: "center", border: "1px solid rgba(122,158,142,0.15)" },
    suppIcon: { fontSize: "2rem", marginBottom: "0.5rem" },
    suppName: { fontSize: "0.85rem", fontWeight: 700, color: COLORS.ink },
    suppDose: { fontSize: "0.78rem", color: COLORS.sageDark, fontWeight: 600, margin: "0.2rem 0" },
    suppWhy: { fontSize: "0.72rem", color: COLORS.muted },
    hospGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", alignItems: "start" },
    hospList: { display: "flex", flexDirection: "column", gap: "0.75rem" },
    hospCard: (sel) => ({ background: sel ? "rgba(122,158,142,0.1)" : COLORS.cream, borderRadius: 14, padding: "1rem 1.25rem", border: `1.5px solid ${sel ? COLORS.sage : "rgba(122,158,142,0.15)"}`, cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "flex-start", gap: "0.75rem" }),
    hospName: { fontSize: "0.9rem", fontWeight: 700, color: COLORS.ink },
    hospAddr: { fontSize: "0.75rem", color: COLORS.muted, marginTop: 2 },
    hospNote: { fontSize: "0.72rem", color: COLORS.sage, marginTop: 2 },
    hospPhone: { fontSize: "0.8rem", color: COLORS.sageDark, fontWeight: 600, marginTop: 4 },
    mapBox: { borderRadius: 18, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.12)", height: 380 },
    emSection: { background: "linear-gradient(135deg,#2A1A1A,#3A2020)", padding: "3rem 1.5rem" },
    emTitle: { fontFamily: "Georgia,serif", fontSize: "1.3rem", fontWeight: 300, color: "#fff", marginBottom: "1rem" },
    emList: { listStyle: "none", padding: 0 },
    emItem: { display: "flex", alignItems: "flex-start", gap: "0.75rem", padding: "0.7rem 0", borderBottom: "1px solid rgba(255,255,255,0.1)", fontSize: "0.87rem", color: "rgba(255,255,255,0.9)" },
    emGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" },
    contactCard: { background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "1rem 1.25rem", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" },
    contactName: { fontSize: "0.85rem", fontWeight: 700, color: "#fff" },
    contactDesc: { fontSize: "0.73rem", color: "rgba(255,255,255,0.55)", marginTop: 1 },
    contactPhone: { fontSize: "0.95rem", fontWeight: 700, color: "#90D0B8" },
    resGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: "1rem" },
    resCard: { background: "#fff", borderRadius: 14, padding: "1.35rem", border: "1px solid rgba(122,158,142,0.15)", textDecoration: "none", color: "inherit", display: "block", transition: "all 0.2s" },
    resTag: { fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", background: "rgba(122,158,142,0.1)", color: COLORS.sageDark, padding: "0.18rem 0.55rem", borderRadius: 50 },
    resName: { fontSize: "0.9rem", fontWeight: 700, color: COLORS.ink, margin: "0.6rem 0 0.35rem" },
    resDesc: { fontSize: "0.77rem", color: COLORS.muted },
    resLink: { fontSize: "0.72rem", color: COLORS.sage, marginTop: "0.6rem" },
    tipsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: "0.9rem" },
    tipCard: { background: COLORS.cream, borderRadius: 14, padding: "1.35rem", border: "1px solid rgba(122,158,142,0.12)", textAlign: "center" },
    tipIcon: { fontSize: "1.9rem", marginBottom: "0.6rem" },
    tipTitle: { fontSize: "0.85rem", fontWeight: 700, color: COLORS.ink, marginBottom: "0.3rem" },
    tipDesc: { fontSize: "0.75rem", color: COLORS.muted },
    footer: { background: COLORS.ink, color: "rgba(255,255,255,0.5)", padding: "2rem 1.5rem", textAlign: "center", fontSize: "0.78rem" },
  };

  const weekData = activeTrimester === "t1" ? T1_WEEKS : activeTrimester === "t2" ? T2_WEEKS : T3_WEEKS;
  const tri = activeTrimester;

  return (
    <div style={s.app}>
      {/* NAV */}
      <nav style={s.nav}>
        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: COLORS.sageDark, marginRight: "0.5rem", whiteSpace: "nowrap" }}>🌿 Pregnancy</span>
        {NAV_ITEMS.map(n => (
          <button key={n.id} style={s.navBtn(n.id)} onClick={() => scrollTo(n.id)}>{n.label}</button>
        ))}
      </nav>

      {/* HERO */}
      <div style={s.hero}>
        <div style={s.maxW}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: COLORS.sage, marginBottom: "0.75rem" }}>🍁 Scarborough · Toronto · Ontario</div>
          <h1 style={s.h1}>Your <em style={{ color: COLORS.sageDark }}>complete</em> pregnancy journey guide</h1>
          <p style={{ color: COLORS.muted, fontSize: "0.9rem", maxWidth: 480, marginBottom: "1.5rem" }}>A step-by-step companion from today through the birth of your baby — tailored for families in Scarborough, Toronto.</p>

          {/* LMP INPUT */}
          <div style={{ background: "#fff", borderRadius: 18, padding: "1.5rem", border: "1.5px solid rgba(122,158,142,0.35)", marginBottom: "1.5rem", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <div style={{ fontWeight: 700, fontSize: "0.9rem", color: COLORS.ink, marginBottom: "0.3rem" }}>🗓️ Enter your Last Menstrual Period (LMP) date</div>
            <div style={{ fontSize: "0.78rem", color: COLORS.muted, marginBottom: "0.9rem" }}>The first day of your last period before pregnancy. All stats below update instantly.</div>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <input
                  type="date"
                  value={lmp}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={e => handleLmpChange(e.target.value)}
                  style={{ padding: "0.6rem 1rem", borderRadius: 10, border: "1.5px solid " + (lmpError ? COLORS.red : COLORS.sage), fontSize: "1rem", fontFamily: "inherit", color: COLORS.ink, background: COLORS.cream, outline: "none", minWidth: 180 }}
                />
                {lmpError && <div style={{ fontSize: "0.73rem", color: COLORS.red, marginTop: "0.3rem" }}>⚠️ {lmpError}</div>}
              </div>
              {info && (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", paddingTop: "0.5rem" }}>
                  <span style={{ background: "rgba(74,122,104,0.12)", color: COLORS.sageDark, fontSize: "0.78rem", fontWeight: 600, padding: "0.3rem 0.75rem", borderRadius: 50 }}>Week {info.weeks}</span>
                  <span style={{ background: "rgba(74,122,104,0.12)", color: COLORS.sageDark, fontSize: "0.78rem", fontWeight: 600, padding: "0.3rem 0.75rem", borderRadius: 50 }}>{info.trimester} Trimester</span>
                  <span style={{ background: "rgba(74,122,104,0.12)", color: COLORS.sageDark, fontSize: "0.78rem", fontWeight: 600, padding: "0.3rem 0.75rem", borderRadius: 50 }}>Due {info.dueDateStr}</span>
                </div>
              )}
            </div>
          </div>

          {info ? (
            <div style={s.statsGrid}>
              <div style={s.statCard}><div style={s.statLabel}>Current Week</div><div style={s.statValue}>Week {info.weeks}</div><div style={s.statSub}>{info.trimester} Trimester · {info.days} day{info.days !== 1 ? "s" : ""} into this week</div></div>
              <div style={s.statCard}><div style={s.statLabel}>Due Date</div><div style={s.statValue}>{info.dueDateStr}</div><div style={s.statSub}>Based on your LMP date</div></div>
              <div style={s.statCard}><div style={s.statLabel}>Baby's Size</div><div style={s.statValue}>{info.sizeEntry[1]} {info.sizeEntry[2]}</div><div style={s.statSub}>Week {info.weeks} estimate</div></div>
              <div style={s.statCard}><div style={s.statLabel}>Weeks Remaining</div><div style={s.statValue}>~{info.weeksLeft} weeks</div><div style={s.statSub}>You're doing amazing!</div></div>
            </div>
          ) : (
            <div style={{ background: "rgba(122,158,142,0.1)", borderRadius: 14, padding: "1.25rem", color: COLORS.muted, fontSize: "0.88rem", marginBottom: "1.5rem", textAlign: "center" }}>
              👆 Enter your LMP date above to see your personalised pregnancy stats
            </div>
          )}

          <div style={s.ctaRow}>
            <button style={s.btnPrimary} onClick={() => scrollTo("timeline")}>📅 View Timeline</button>
            <button style={s.btnSecondary} onClick={() => scrollTo("hospitals")}>🏥 Find Hospitals</button>
            <button style={s.btnSecondary} onClick={() => scrollTo("emergency")}>🚨 Emergency Info</button>
          </div>
        </div>
      </div>

      {/* TIMELINE */}
      <div id="timeline" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Week by Week</div>
          <h2 style={s.sectionTitle}>Your Pregnancy Timeline</h2>
          <p style={s.sectionSub}>What to expect, do, and watch for — July 2026 through March 2027.</p>
          <div style={s.tabRow}>
            {[["t1","🟢 First Trimester · Wks 1–12"],["t2","🟡 Second Trimester · Wks 13–26"],["t3","🔴 Third Trimester · Wks 27–40"]].map(([id,label]) => (
              <button key={id} style={s.tab(id)} onClick={() => setActiveTrimester(id)}>{label}</button>
            ))}
          </div>
          <div style={s.weeksGrid}>
            {weekData.map((w, i) => (
              <div key={i} style={s.weekCard(tri)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <span style={s.badge(tri)}>{w.badge}</span>
                  <span style={{ fontSize: "1.75rem" }}>{w.icon}</span>
                </div>
                <div style={s.weekTitle}>{w.title}</div>
                <div style={s.weekDate}>{w.date}</div>
                <ul style={s.weekItems}>
                  {w.items.map((item, j) => (
                    <li key={j} style={s.weekItem}><span style={{ color: COLORS.sage, flexShrink: 0 }}>→</span>{item}</li>
                  ))}
                </ul>
                <div style={s.testTag}>{w.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* NUTRITION */}
      <div id="nutrition" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Eat Well</div>
          <h2 style={s.sectionTitle}>Pregnancy Nutrition Guide</h2>
          <p style={s.sectionSub}>What to eat more of, and what to avoid for a healthy pregnancy.</p>
          <div style={s.foodGrid}>
            <div>
              <div style={s.foodColTitle}>✅ Eat More Of</div>
              <div style={s.foodList}>
                {SAFE_FOODS.map((f, i) => (
                  <div key={i} style={s.foodItem(true)}>
                    <span style={s.foodEmoji}>{f.emoji}</span>
                    <div><div style={s.foodName}>{f.name}</div><div style={s.foodDesc}>{f.desc}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={s.foodColTitle}>❌ Avoid or Limit</div>
              <div style={s.foodList}>
                {AVOID_FOODS.map((f, i) => (
                  <div key={i} style={s.foodItem(false)}>
                    <span style={s.foodEmoji}>{f.emoji}</span>
                    <div><div style={s.foodName}>{f.name}</div><div style={s.foodDesc}>{f.desc}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EXERCISE */}
      <div id="exercise" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Stay Active</div>
          <h2 style={s.sectionTitle}>Safe Exercises By Trimester</h2>
          <p style={s.sectionSub}>Aim for 30 minutes of gentle movement daily. Always check with your doctor first.</p>
          <div style={s.exGrid}>
            {EXERCISES.map((ex, i) => (
              <div key={i} style={s.exCard(ex.bg)}>
                <div style={s.exTitle(ex.color)}>{ex.label}</div>
                <div style={s.exWeeks(ex.color)}>{ex.weeks}</div>
                <ul style={s.exList}>
                  {ex.list.map((item, j) => (
                    <li key={j} style={s.exItem}><span style={{ fontSize: "1.1rem" }}>{item.icon}</span>{item.name}</li>
                  ))}
                </ul>
                <div style={s.exAvoid}>⚠️ Avoid: {ex.avoid}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SUPPLEMENTS */}
      <div id="supplements" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Daily Essentials</div>
          <h2 style={s.sectionTitle}>Prenatal Supplements</h2>
          <p style={s.sectionSub}>Check your prenatal vitamin label — many are already included. Always confirm with your doctor.</p>
          <div style={s.suppGrid}>
            {SUPPLEMENTS.map((sp, i) => (
              <div key={i} style={s.suppCard}>
                <div style={s.suppIcon}>{sp.icon}</div>
                <div style={s.suppName}>{sp.name}</div>
                <div style={s.suppDose}>{sp.dose}</div>
                <div style={s.suppWhy}>{sp.why}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOSPITALS */}
      <div id="hospitals" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Scarborough · Toronto</div>
          <h2 style={s.sectionTitle}>Nearby Hospitals & Maternity Centres</h2>
          <p style={s.sectionSub}>All hospitals below offer maternity and labour & delivery services near Scarborough. Click a hospital to open Google Maps.</p>
          <div style={s.hospGrid}>
            <div style={s.hospList}>
              {HOSPITALS.map((h, i) => (
                <div key={i} style={s.hospCard(selectedHospital === i)} onClick={() => setSelectedHospital(i)}>
                  <span style={{ fontSize: "1.75rem", flexShrink: 0 }}>🏥</span>
                  <div>
                    <div style={s.hospName}>{h.name}</div>
                    <div style={s.hospAddr}>{h.addr}</div>
                    <div style={s.hospNote}>{h.note}</div>
                    <div style={s.hospPhone}>📞 <a href={`tel:${h.phone.replace(/-/g,"")}`} style={{ color: COLORS.sageDark, textDecoration: "none" }}>{h.phone}</a></div>
                  </div>
                </div>
              ))}
            </div>
            <div>
              {/* Selected hospital detail card */}
              <div style={{ background: COLORS.cream, borderRadius: 18, padding: "1.75rem", border: `1.5px solid ${COLORS.sage}`, marginBottom: "1rem" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🏥</div>
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", fontWeight: 400, color: COLORS.ink, marginBottom: "0.3rem" }}>{HOSPITALS[selectedHospital].name}</div>
                <div style={{ fontSize: "0.82rem", color: COLORS.muted, marginBottom: "0.25rem" }}>📍 {HOSPITALS[selectedHospital].addr}</div>
                <div style={{ fontSize: "0.82rem", color: COLORS.sage, marginBottom: "1rem" }}>{HOSPITALS[selectedHospital].note}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                  <a
                    href={HOSPITALS[selectedHospital].maps}
                    target="_blank" rel="noopener noreferrer"
                    style={{ background: COLORS.sageDark, color: "#fff", borderRadius: 50, padding: "0.7rem 1.25rem", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none", textAlign: "center" }}
                  >
                    🗺️ Open in Google Maps
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(HOSPITALS[selectedHospital].addr + " Toronto Ontario")}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ background: "transparent", color: COLORS.sageDark, border: `1.5px solid ${COLORS.sageDark}`, borderRadius: 50, padding: "0.7rem 1.25rem", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none", textAlign: "center" }}
                  >
                    🚗 Get Driving Directions
                  </a>
                  <a
                    href={`tel:${HOSPITALS[selectedHospital].phone.replace(/-/g,"")}`}
                    style={{ background: "transparent", color: COLORS.terra, border: `1.5px solid ${COLORS.terra}`, borderRadius: 50, padding: "0.7rem 1.25rem", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none", textAlign: "center" }}
                  >
                    📞 Call {HOSPITALS[selectedHospital].phone}
                  </a>
                </div>
              </div>
              <div style={{ background: "#fff", borderRadius: 12, padding: "1rem 1.1rem", fontSize: "0.78rem", color: COLORS.muted, border: "1px solid rgba(122,158,142,0.15)" }}>
                💡 <strong>Tip:</strong> Click any hospital on the left to see options. Pre-register before week 36 and always bring your OHIP card to every visit.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EMERGENCY */}
      <div id="emergency" style={s.emSection}>
        <div style={s.maxW}>
          <div style={{ ...s.sectionEye, color: "#F0A090" }}>Know the Signs</div>
          <h2 style={{ ...s.sectionTitle, color: "#fff" }}>When to Seek Immediate Help</h2>
          <p style={{ ...s.sectionSub, color: "rgba(255,255,255,0.6)" }}>Trust your instincts. When in doubt, always call or go to hospital immediately.</p>
          <div style={s.emGrid}>
            <div>
              <div style={s.emTitle}>🚨 Go to Hospital Immediately If:</div>
              <ul style={s.emList}>
                {EMERGENCY_SIGNS.map((sg, i) => (
                  <li key={i} style={s.emItem}><span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{sg.icon}</span>{sg.text}</li>
                ))}
              </ul>
            </div>
            <div>
              <div style={s.emTitle}>📞 Emergency Contacts</div>
              {CONTACTS.map((c, i) => (
                <div key={i} style={s.contactCard}>
                  <span style={{ fontSize: "1.6rem" }}>{c.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={s.contactName}>{c.name}</div>
                    <div style={s.contactDesc}>{c.desc}</div>
                  </div>
                  <a href={`tel:${c.tel}`} style={s.contactPhone}>{c.phone}</a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RESOURCES */}
      <div id="resources" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Helpful Links</div>
          <h2 style={s.sectionTitle}>Resources & Support in Ontario</h2>
          <p style={s.sectionSub}>Trusted resources for pregnancy care in Toronto and across Ontario.</p>
          <div style={s.resGrid}>
            {RESOURCES.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" style={s.resCard}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.1)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "1.4rem" }}>{r.icon}</span>
                  <span style={s.resTag}>{r.tag}</span>
                </div>
                <div style={s.resName}>{r.name}</div>
                <div style={s.resDesc}>{r.desc}</div>
                <div style={s.resLink}>↗ Visit resource</div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* TIPS */}
      <div id="tips" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Minor But Important</div>
          <h2 style={s.sectionTitle}>Daily Tips for a Healthy Pregnancy</h2>
          <p style={s.sectionSub}>Small habits that make a big difference throughout your pregnancy journey.</p>
          <div style={s.tipsGrid}>
            {TIPS.map((t, i) => (
              <div key={i} style={s.tipCard}>
                <div style={s.tipIcon}>{t.icon}</div>
                <div style={s.tipTitle}>{t.title}</div>
                <div style={s.tipDesc}>{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* HOSPITAL BAG CHECKLIST */}
      <div id="checklist" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Pack by Week 36</div>
          <h2 style={s.sectionTitle}>Hospital Bag & Car Seat Checklist</h2>
          <p style={s.sectionSub}>Tick off items as you pack. Your progress is saved while you're on this page.</p>
          <ChecklistSection />
        </div>
      </div>


      {/* SHOP */}
      <div id="shop" style={s.section("#fff")}>
        <div style={s.maxW}>
          {/* Affiliate Disclosure */}
          <div style={{ background: "rgba(201,168,76,0.1)", border: "1.5px solid rgba(201,168,76,0.3)", borderRadius: 14, padding: "0.85rem 1.25rem", marginBottom: "2rem", fontSize: "0.78rem", color: COLORS.muted, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
            <span style={{ fontSize: "1rem", flexShrink: 0 }}>ℹ️</span>
            <span><strong style={{ color: COLORS.ink }}>Affiliate Disclosure:</strong> As an Amazon Associate, we earn from qualifying purchases. All links go to Amazon.ca and are marked with your tracking ID. This is at <strong>no extra cost to you</strong> — and helps support this free pregnancy guide. We only recommend products relevant to pregnancy and baby care.</span>
          </div>

          <div style={s.sectionEye}>Amazon.ca Recommendations</div>
          <h2 style={s.sectionTitle}>Shop Pregnancy Essentials</h2>
          <p style={s.sectionSub}>Handpicked products for every stage of your pregnancy journey — all available on Amazon.ca.</p>

          <ShopSection />
        </div>
      </div>


      {/* BLOG */}
      <div id="blog" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Articles & Guides</div>
          <h2 style={s.sectionTitle}>Pregnancy Blog</h2>
          <p style={s.sectionSub}>In-depth guides for Canadian families — written to help you navigate every stage of pregnancy with confidence.</p>
          <BlogSection />
        </div>
      </div>

      {/* ABOUT */}
      <div id="about" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Our Story</div>
          <h2 style={s.sectionTitle}>About This Guide</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", marginTop: "1rem" }}>
            <div>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🌿</div>
              <h3 style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", fontWeight: 400, marginBottom: "0.75rem", color: COLORS.ink }}>Built With Love for a Growing Family</h3>
              <p style={{ fontSize: "0.88rem", color: COLORS.muted, lineHeight: 1.8, marginBottom: "1rem" }}>
                This pregnancy guide was created by a family in Scarborough, Toronto, Ontario — for families everywhere. When we were navigating our own pregnancy journey, we found it hard to find resources that were practical, locally relevant, and easy to understand.
              </p>
              <p style={{ fontSize: "0.88rem", color: COLORS.muted, lineHeight: 1.8, marginBottom: "1rem" }}>
                So we built this — a free, interactive, week-by-week pregnancy companion that covers everything from nutrition and exercise to local hospitals and emergency contacts. We wanted every expectant parent to have access to clear, trustworthy information regardless of their background or experience.
              </p>
              <p style={{ fontSize: "0.88rem", color: COLORS.muted, lineHeight: 1.8 }}>
                Based in Scarborough, Toronto, Ontario 🍁 — with a heart for families worldwide 🌍
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {[
                { icon: "🎯", title: "Our Mission", desc: "To provide free, accurate, and locally relevant pregnancy information to every expectant family — regardless of background or experience." },
                { icon: "💚", title: "Our Values", desc: "Evidence-based information, cultural sensitivity, and genuine care for the wellbeing of mothers, babies, and families." },
                { icon: "🔒", title: "Your Privacy", desc: "We do not collect personal information. Your LMP date stays on your device and is never sent to our servers." },
                { icon: "⚠️", title: "Medical Disclaimer", desc: "This guide is for informational purposes only and does not replace professional medical advice. Always consult your doctor or midwife." },
              ].map((item, i) => (
                <div key={i} style={{ background: COLORS.cream, borderRadius: 14, padding: "1rem 1.25rem", border: "1px solid rgba(122,158,142,0.15)", display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "1.5rem", flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: COLORS.ink, marginBottom: "0.25rem" }}>{item.title}</div>
                    <div style={{ fontSize: "0.78rem", color: COLORS.muted, lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* PRIVACY POLICY */}
      <div id="privacy" style={s.section(COLORS.cream)}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Legal</div>
          <h2 style={s.sectionTitle}>Privacy Policy</h2>
          <p style={{ fontSize: "0.78rem", color: COLORS.muted, marginBottom: "2rem" }}>Last updated: August 2026</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {[
              { title: "1. Introduction", content: "Welcome to Your Pregnancy Guide (yourpregnancyguide.com). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard information when you visit our website." },
              { title: "2. Information We Collect", content: "We do not collect personally identifiable information. The LMP date you enter is processed entirely on your device and is never transmitted to our servers. We use Google Analytics to collect anonymous usage data including pages visited, time spent on site, device type, and general geographic location (country/city level). This data is aggregated and cannot be used to identify individual users." },
              { title: "3. Google Analytics", content: "We use Google Analytics to understand how visitors use our site. Google Analytics uses cookies to collect anonymous information about your visit. You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on available at tools.google.com/dlpage/gaoptout. For more information on Google's privacy practices visit google.com/policies/privacy." },
              { title: "4. Google AdSense", content: "We use Google AdSense to display advertisements on our website. Google AdSense uses cookies to serve ads based on your prior visits to our website and other sites on the internet. You may opt out of personalized advertising by visiting Google's Ads Settings at adssettings.google.com. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to our site and other sites on the internet." },
              { title: "5. Amazon Affiliate Links", content: "This website participates in the Amazon Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.ca. When you click on Amazon product links on our site and make a purchase, we may earn a small commission at no additional cost to you. Amazon may use cookies to track purchases. For more information see Amazon's Privacy Notice at amazon.ca/privacy." },
              { title: "6. Cookies", content: "Our website uses cookies — small text files stored on your device — for the purposes of Google Analytics tracking and Google AdSense advertising. You can control cookies through your browser settings. Disabling cookies may affect the functionality of some features on our site." },
              { title: "7. Third Party Links", content: "Our website contains links to third-party websites including hospitals, government resources, and Amazon.ca. We are not responsible for the privacy practices of these external sites and encourage you to review their privacy policies." },
              { title: "8. Children's Privacy", content: "This website is not directed at children under 13 years of age. We do not knowingly collect personal information from children." },
              { title: "9. Your Rights in Canada", content: "Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and Ontario's privacy laws, you have the right to access information we hold about you and request corrections. As we do not collect personal information, these rights are largely not applicable. For questions about your privacy rights contact us at the email below." },
              { title: "10. Contact Us", content: "If you have questions about this Privacy Policy please contact us at: yourpregnancyguide.com/contact" },
            ].map((section, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 14, padding: "1.25rem 1.5rem", border: "1px solid rgba(122,158,142,0.15)" }}>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: "1rem", fontWeight: 600, color: COLORS.ink, marginBottom: "0.6rem" }}>{section.title}</h3>
                <p style={{ fontSize: "0.83rem", color: COLORS.muted, lineHeight: 1.8 }}>{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div id="contact" style={s.section("#fff")}>
        <div style={s.maxW}>
          <div style={s.sectionEye}>Get In Touch</div>
          <h2 style={s.sectionTitle}>Contact Us</h2>
          <p style={s.sectionSub}>Have a question, suggestion, or want to share your pregnancy story? We would love to hear from you.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", marginTop: "1rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {[
                { icon: "📍", title: "Location", desc: "Scarborough, Toronto, Ontario, Canada 🍁" },
                { icon: "🌍", title: "Serving", desc: "Families worldwide — with a focus on Canadian pregnancy resources" },
                { icon: "📧", title: "Email", desc: "contact@yourpregnancyguide.com" },
                { icon: "⏰", title: "Response Time", desc: "We aim to respond within 2-3 business days" },
              ].map((item, i) => (
                <div key={i} style={{ background: COLORS.cream, borderRadius: 14, padding: "1.25rem", border: "1px solid rgba(122,158,142,0.15)", display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  <span style={{ fontSize: "1.75rem" }}>{item.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: COLORS.ink }}>{item.title}</div>
                    <div style={{ fontSize: "0.82rem", color: COLORS.muted, marginTop: "0.2rem" }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: COLORS.cream, borderRadius: 18, padding: "1.75rem", border: "1px solid rgba(122,158,142,0.2)" }}>
              <h3 style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", fontWeight: 400, marginBottom: "1.25rem", color: COLORS.ink }}>Send Us a Message</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                <input type="text" placeholder="Your name" style={{ padding: "0.7rem 1rem", borderRadius: 10, border: "1.5px solid rgba(122,158,142,0.3)", fontSize: "0.88rem", fontFamily: "inherit", background: "#fff", outline: "none", color: COLORS.ink }} />
                <input type="email" placeholder="Your email address" style={{ padding: "0.7rem 1rem", borderRadius: 10, border: "1.5px solid rgba(122,158,142,0.3)", fontSize: "0.88rem", fontFamily: "inherit", background: "#fff", outline: "none", color: COLORS.ink }} />
                <select style={{ padding: "0.7rem 1rem", borderRadius: 10, border: "1.5px solid rgba(122,158,142,0.3)", fontSize: "0.88rem", fontFamily: "inherit", background: "#fff", outline: "none", color: COLORS.ink }}>
                  <option>General question</option>
                  <option>Content suggestion</option>
                  <option>Report an error</option>
                  <option>Partnership inquiry</option>
                  <option>Other</option>
                </select>
                <textarea placeholder="Your message..." rows={4} style={{ padding: "0.7rem 1rem", borderRadius: 10, border: "1.5px solid rgba(122,158,142,0.3)", fontSize: "0.88rem", fontFamily: "inherit", background: "#fff", outline: "none", color: COLORS.ink, resize: "vertical" }} />
                <button style={{ background: COLORS.sageDark, color: "#fff", border: "none", borderRadius: 50, padding: "0.8rem", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}>
                  📬 Send Message
                </button>
                <p style={{ fontSize: "0.72rem", color: COLORS.muted, textAlign: "center" }}>We read every message and respond within 2-3 business days.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={s.footer}>
        <p>Made with 💚 for a family in <strong style={{ color: "#fff" }}>Scarborough, Toronto</strong>{info ? ` · Week ${info.weeks} · Due ${info.dueDateStr}` : ""}</p>
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap", marginTop: "0.75rem" }}>
          {["about","privacy","contact","blog"].map(id => (
            <button key={id} onClick={() => scrollTo(id)} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", cursor: "pointer", textTransform: "capitalize" }}>{id.charAt(0).toUpperCase() + id.slice(1)}</button>
          ))}
        </div>
        <p style={{ marginTop: "0.4rem" }}>⚠️ For informational purposes only. Always follow your doctor's or midwife's personal advice.</p>
        <p style={{ marginTop: "0.4rem", color: "rgba(255,255,255,0.35)" }}>As an Amazon Associate we earn from qualifying purchases · <a href="https://associates.amazon.ca" target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Amazon Associates Program</a></p>
      </div>
    </div>
  );
}
